import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@hooks/redux'
import { validateUser } from '@store/slices/authSlice'
import { Box } from '@mui/material'

// Layouts
import MainLayout from '@components/layouts/MainLayout'
import AuthLayout from '@components/layouts/AuthLayout'

// Pages
import SignIn from '@pages/auth/SignIn'
import SignUp from '@pages/auth/SignUp'
import ForgotPassword from '@pages/auth/ForgotPassword'
import ResetPassword from '@pages/auth/ResetPassword'
import Home from '@pages/Home'
import Measurements from '@pages/Measurements'
import Alerts from '@pages/Alerts'
import Devices from '@pages/Devices'
import DeviceDetail from '@pages/DeviceDetail'
import Profile from '@pages/Profile'
import ApiaryCreate from '@pages/apiaries/ApiaryCreate'
import ApiaryEdit from '@pages/apiaries/ApiaryEdit'
import HiveCreate from '@pages/hives/HiveCreate'
import HiveEdit from '@pages/hives/HiveEdit'
import HiveDetail from '@pages/hives/HiveDetail'
import HiveInspections from '@pages/hives/HiveInspections'
import Inspect from '@pages/inspections/Inspect'
import InspectionCreate from '@pages/inspections/InspectionCreate'
import Inspections from '@pages/inspections/Inspections'
import ChecklistsPage from '@pages/checklists/ChecklistsPage'
import AlertRules from '@pages/alerts/AlertRules'
import AlertRuleEdit from '@pages/alerts/AlertRuleEdit'
import AlertRulesDefault from '@pages/alerts/AlertRulesDefault'
import DashboardEdit from '@pages/dashboards/DashboardEdit'
import Dashboards from '@pages/dashboards/Dashboards'
import DashboardView from '@pages/dashboards/DashboardView'
import Research from '@pages/Research'
import Groups from '@pages/groups/Groups'
import GroupEdit from '@pages/groups/GroupEdit'
import GroupCreate from '@pages/groups/GroupCreate'
import ExportData from '@pages/data/ExportData'
import ImportData from '@pages/data/ImportData'
import ApiaryManagement from '@pages/apiaries/ApiaryManagement'
import AdminDashboard from '@pages/admin/AdminDashboard'
import NotFound from '@pages/NotFound'

// Components
import LoadingScreen from '@components/common/LoadingScreen'
import PWAUpdatePrompt from '@components/common/PWAUpdatePrompt'
import ProtectedRoute from '@components/auth/ProtectedRoute'

const App: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isAuthenticated, isLoading, token } = useAppSelector((state) => state.auth)

  useEffect(() => {
    // Validate user on app start if token exists
    if (token && !isAuthenticated) {
      dispatch(validateUser())
    }
  }, [dispatch, token, isAuthenticated])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <PWAUpdatePrompt />
      <Routes>
        {/* Auth Routes */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="sign-in" element={<SignIn />} />
          <Route path="sign-up" element={<SignUp />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password/:uid/:token" element={<ResetPassword />} />
        </Route>

        {/* Protected Routes */}
        <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
          <Route index element={<Home />} />
          <Route path="apiaries" element={<Home />} />
          <Route path="measurements" element={<Measurements />} />
          <Route path="measurements/:id" element={<Measurements />} />
          <Route path="alerts" element={<Alerts />} />
          <Route path="devices" element={<Devices />} />
          <Route path="devices/:id" element={<DeviceDetail />} />
          <Route path="profile" element={<Profile />} />

          {/* Apiary Routes */}
          <Route path="apiaries/create" element={<ApiaryCreate />} />
          <Route path="apiaries/:id/edit" element={<ApiaryEdit />} />
          <Route path="apiaries/:id/management" element={<ApiaryManagement />} />

          {/* Hive Routes */}
          <Route path="hives/create" element={<HiveCreate />} />
          <Route path="hives/:id/edit" element={<HiveEdit />} />
          <Route path="hives/:id/inspections" element={<HiveInspections />} />
          <Route path="hives/:id/inspections/:inspectionId" element={<Inspect />} />
          <Route path="hives/:id" element={<HiveDetail />} />

          {/* Inspection Routes */}
          <Route path="inspections" element={<Inspections />} />
          <Route path="inspections/create" element={<InspectionCreate />} />
          <Route path="inspections/:id" element={<Inspect />} />


          {/* Checklist Routes */}
          <Route path="checklists" element={<ChecklistsPage />} />
          <Route path="checklists/create" element={<ChecklistsPage />} />

          {/* Alert Routes */}
          <Route path="alertrules" element={<AlertRules />} />
          <Route path="alertrules/create" element={<AlertRuleEdit />} />
          <Route path="alertrules/:id/edit" element={<AlertRuleEdit />} />
          <Route path="alertrules/default" element={<AlertRulesDefault />} />

          {/* Dashboard Routes */}
          <Route path="dashboards" element={<Dashboards />} />
          <Route path="dashboards/create" element={<DashboardEdit />} />
          <Route path="dashboards/:id/edit" element={<DashboardEdit />} />
          <Route path="dashboards/:id" element={<DashboardView />} />

          {/* Group Routes */}
          <Route path="groups" element={<Groups />} />
          <Route path="groups/create" element={<GroupCreate />} />
          <Route path="groups/:id/edit" element={<GroupEdit />} />

          <Route path="export" element={<ExportData />} />
          <Route path="import" element={<ImportData />} />

          {/* Admin Routes (system-wide, not a personal beekeeper view - gated on User.admin) */}
          <Route path="admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />

          {/* Other Routes */}
          <Route path="research" element={<Research />} />
        </Route>

        {/* Redirect /sign-in to /auth/sign-in */}
        <Route path="/sign-in" element={<Navigate to="/auth/sign-in" replace />} />

        {/* 404 Route */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" replace />} />
      </Routes>
    </Box>
  )
}

export default App
